<template lang="pug">
div
  v-overlay(
    :value="$vuetify.breakpoint.mdAndDown && !mini"
    @click="mini = true"
  )
  v-navigation-drawer.ev-navigation-drawer(
    v-model="drawer"
    color="base"
    app
    permanent
    hide-overlay
    floating
    :mini-variant="mini"
    :width="currentDrawerWidth"
  )
    .ev-navigation-drawer__content
      v-responsive(
        v-if="!hideFirstSubdrawer"
        :max-width="drawerWidth"
      )
        .ev-drawer-content.base
          ev-drawer-content(:mini.sync="mini")

      v-slide-x-reverse-transition
        v-responsive.ma(
          v-if="subdrawer"
          :max-width="subdrawerWidth"
        )
          .ev-subdrawer-content
            component(
              :is="SUBDRAWER_MODES[subdrawer]"
              v-bind="subdrawerAttrs"
            )
</template>

<script>
import { DRAWER_TYPES } from '@/constants/drawer.js';

import EvDrawerContent from './EvDrawerContent.vue';

import EvNavigationDrawerSubmenu from './EvDrawerSubmenu.vue';
import LoginForm from '@/modules/Auth/LoginForm.vue';

const SUBDRAWER_MODES = {
  [DRAWER_TYPES.portal]: 'portal-target',
  [DRAWER_TYPES.login]: LoginForm,
  [DRAWER_TYPES.menu]: EvNavigationDrawerSubmenu,
};

const DRAWER_WIDHT = 260;
const SUBDRAWER_WIDHT = 260;
const MINI_DRAWER_WIDTH = 56;
const COLLAPSED_SUBDRAWER_WIDTH = 88;

export default {
  components: {
    EvDrawerContent,
  },
  data() {
    return {
      drawer: false,
      mini: this.$vuetify.breakpoint.mdAndDown,
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
      isSubdrawerCollapsed: false,

      subdrawer: null,

      SUBDRAWER_MODES,
    };
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.mdAndDown;
    },
    isSubdrawerCollapsible() {
      return !!this.$route?.meta?.subdrawerCollapsible;
    },
    drawerWidth() {
      if (this.isMobile && this.$drawer.subdrawer) {
        return MINI_DRAWER_WIDTH;
      }
      return DRAWER_WIDHT;
    },
    expandedSubdrawerWidth() {
      const defaultWidth = this.$route?.meta?.subdrawerWidth || SUBDRAWER_WIDHT;
      if (!this.isMobile || !this.viewportWidth) {
        return defaultWidth;
      }

      const reservedWidth = this.hideFirstSubdrawer && this.subdrawer ? 0 : this.drawerWidth;
      const availableWidth = Math.max(this.viewportWidth - reservedWidth, MINI_DRAWER_WIDTH);

      return Math.min(defaultWidth, availableWidth);
    },
    subdrawerWidth() {
      if (this.isSubdrawerCollapsible && this.isSubdrawerCollapsed) {
        return COLLAPSED_SUBDRAWER_WIDTH;
      }

      return this.expandedSubdrawerWidth;
    },
    hideFirstSubdrawer() {
      return this.$route?.meta?.hideFirstSubdrawer || false;
    },
    currentDrawerWidth() {
      const { subdrawer, drawerWidth, subdrawerWidth, hideFirstSubdrawer } = this;
      if (subdrawer && hideFirstSubdrawer) return subdrawerWidth;
      return subdrawer ? drawerWidth + subdrawerWidth : drawerWidth;
    },
    subdrawerAttrs() {
      if (this.subdrawer === DRAWER_TYPES.portal) {
        return {
          name: 'subdrawer',
          slotProps: {
            isSubdrawerCollapsed: this.isSubdrawerCollapsed,
            isSubdrawerCollapsible: this.isSubdrawerCollapsible,
            toggleSubdrawerCollapsed: this.toggleSubdrawerCollapsed,
          },
        };
      }

      return {};
    },

  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  watch: {
    '$route.path': {
      handler() {
        const { meta } = this.$route;
        this.subdrawer = meta.subdrawer;
        this.isSubdrawerCollapsed = false;
        if (meta.drawerChildren) {
          this.subdrawer = this.$conf.DRAWER_TYPES.menu;
        }
        if (this.$vuetify.breakpoint.mdAndDown) {
          this.mini = true;
        }
        this.$drawer.subdrawerSet(this.subdrawer);
      },
      immediate: true,
    },
    '$drawer.subdrawer': {
      handler(val) {
        this.subdrawer = val;
        if (val) {
          this.mini = false;
        } else {
          this.isSubdrawerCollapsed = false;
        }
      },
    },
  },
  methods: {
    handleResize() {
      this.viewportWidth = window.innerWidth;
    },
    toggleSubdrawerCollapsed() {
      if (!this.isSubdrawerCollapsible) {
        return;
      }

      this.isSubdrawerCollapsed = !this.isSubdrawerCollapsed;
    },
  },
};
</script>

<style lang="scss" scoped>
$DRAWER_WIDHT: 260px;

.ev-navigation-drawer {
  font-family: Comfortaa, sans-serif;
  height: calc(100vh - 40px) !important;
  margin: 20px;
  border-radius: 25px 0 0 25px;

  @media screen and (max-width: 768px) {
    margin: 0;
    height: 100vh !important;
    border-radius: 0;
  }

  *::-webkit-scrollbar {
    width: 2px;
    height: 2px;
  }

  *::-webkit-scrollbar-thumb {
    background-color: #ecedf9;
    border-radius: 5px;
  }

  &__content {
    display: flex;
    height: 100%;
    color: #ffeb3b;
    background-color: #{$white} !important;

    .ev-drawer-content {
      display: flex;
      flex-direction: column;
      overflow: auto;
      height: 100%;
      width: $DRAWER_WIDHT;

      &__top {
        padding-left: 10px;
      }
    }

    .ev-subdrawer-content {
      overflow: auto;
      height: 100%;
      position: relative;

      .vue-portal-target {
        height: inherit;
      }
    }
  }

  ::v-deep &__content {
    .v-list-item,
    .v-list-group {
      &__icon {
        margin-right: 15px !important;
      }
    }

    .v-list-group {
      border-radius: 25px 0 0 25px;

      &--active {
        background: #{$primaryLighten} !important;
      }

      &__items {
        font-size: 0.8rem;
        border-radius: 25px;
        background: #{$primaryLighten} !important;

        .v-list-item {
          color: #{$base};
          height: 50px;

          &__icon {
            align-self: center;

            .v-icon {
              color: #{$base};
            }
          }

          &--active {
            font-weight: bold;
          }
        }
      }
    }

    .ev-navigation-drawer--submenu {
      display: flex;
      flex-direction: column;
      height: inherit;

      .v-list-item {
        color: #737373;

        &__title,
        &__subtitle {
          color: inherit;
        }

        &--active {
          background-color: #{$primary} !important;
          color: #ecedf9;

          &::before {
            top: -40px;
            box-shadow: 20px 20px 0 -2px #{$primary} !important;
          }

          &::after {
            bottom: -40px;
            box-shadow: 20px -20px 0 -2px #{$primary} !important;
          }
        }
      }
    }

    .v-list-item {
      padding: 0 16px;

      &:not(.v-list-group__header) {
        margin: 5px 0;
      }

      .v-icon {
        font-size: 18px;
      }

      &::before,
      &::after {
        transition: unset;
      }

      &,
      &:not(.v-list-item--active):hover::before,
      &:not(.v-list-item--active):focus::before,
      &--active {
        border-radius: 25px 0 0 25px;
      }

      &--active {
        position: relative;
        background-color: #{$primaryLighten} !important;
        color: #{$base};
        z-index: 1;

        &::before,
        &::after {
          content: '';
          position: absolute;
          width: 50px;
          height: 40px;
          min-height: initial;
          background-color: transparent;
          right: 0;
          left: initial;
          opacity: 1 !important;
          border-radius: 50%;
          z-index: -1;
        }

        &::before {
          top: -40px;
          box-shadow: 20px 20px 0 -2px #{$primaryLighten} !important;
        }

        &::after {
          bottom: -40px;
          box-shadow: 20px -20px 0 -2px #{$primaryLighten} !important;
        }
      }
    }

    .v-list-group {
      &--active {
        position: relative;
        background-color: #{$primaryLighten} !important;
        color: #{$primary};
        z-index: 1;

        &::before,
        &::after {
          content: '';
          position: absolute;
          width: 50px;
          height: 40px;
          min-height: initial;
          background-color: transparent;
          right: 0;
          left: initial;
          opacity: 1 !important;
          border-radius: 50%;
          z-index: -1;
        }

        &::before {
          top: -40px;
          box-shadow: 20px 20px 0 -2px #{$primaryLighten} !important;
        }

        &::after {
          bottom: -40px;
          box-shadow: 20px -20px 0 -2px #{$primaryLighten} !important;
        }
      }
    }
  }
}
</style>
